import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Word swap sequence — punchy, on-brand, ends on the offer
const SEQUENCE = [
  { text: "INVISIBLE.", duration: 1500 },
  { text: "OFFLINE.", duration: 1500 },
  { text: "OUTDATED.", duration: 1500 },
  { text: "NOT ANYMORE.", duration: 2000 },
];

// Final locked line shown last, then overlay fades out
const FINAL_LINE = {
  pre: "FIRST DRAFT IN AS LITTLE AS",
  highlight: "48 HOURS",
  duration: 3500,
};

// Brief blank-screen pre-roll before the first word appears
const PRE_ROLL_DURATION = 700;

// Step values:
//   -2 = pre-roll (blank screen)
//   0..SEQUENCE.length-1 = current word index
//   SEQUENCE.length = final lockup
//   -1 = fading out
const IntroOverlay = () => {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(-2);

  useEffect(() => {
    // Always play on mount — fresh page load / refresh triggers it again.
    setActive(true);
  }, []);

  useEffect(() => {
    if (!active) return;
    if (step === -2) {
      const t = setTimeout(() => setStep(0), PRE_ROLL_DURATION);
      return () => clearTimeout(t);
    }
    if (step >= 0 && step < SEQUENCE.length) {
      const t = setTimeout(() => setStep((s) => s + 1), SEQUENCE[step].duration);
      return () => clearTimeout(t);
    }
    if (step === SEQUENCE.length) {
      const t = setTimeout(() => setStep(-1), FINAL_LINE.duration);
      return () => clearTimeout(t);
    }
    if (step === -1) {
      const t = setTimeout(() => {
        setActive(false);
      }, 600);
      return () => clearTimeout(t);
    }
  }, [active, step]);

  const isPreRoll = step === -2;
  const isFinal = step === SEQUENCE.length;
  const isDone = step === -1;

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key="intro-overlay"
          initial={{ opacity: 1 }}
          animate={{ opacity: isDone ? 0 : 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          className="fixed inset-0 z-[100] bg-[#050505] flex items-center justify-center px-6 overflow-hidden"
          aria-hidden="true"
          data-testid="intro-overlay"
        >
          {/* subtle orange glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] max-w-[800px] max-h-[800px] rounded-full bg-[#FF4500] opacity-[0.08] blur-3xl" />
          </div>

          <div className="relative z-10 text-center w-full max-w-5xl">
            <AnimatePresence mode="wait">
              {!isPreRoll && !isFinal && step >= 0 && step < SEQUENCE.length && (
                <motion.h2
                  key={`word-${step}`}
                  initial={{ opacity: 0, y: 24, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -24, filter: "blur(10px)" }}
                  transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
                  className="font-['Oswald'] text-white uppercase tracking-tight font-bold text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-none"
                >
                  {SEQUENCE[step].text}
                </motion.h2>
              )}

              {isFinal && (
                <motion.div
                  key="final"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
                  className="flex flex-col items-center gap-3 sm:gap-4"
                >
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                    className="font-['Manrope'] text-[#888888] uppercase tracking-[0.3em] text-xs sm:text-sm"
                  >
                    Wiltshire Web Studio
                  </motion.p>
                  <motion.h2
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.18, duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
                    className="font-['Oswald'] text-white uppercase font-bold tracking-tight text-3xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05]"
                  >
                    {FINAL_LINE.pre}
                    <br />
                    <motion.span
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.45, duration: 0.5, type: "spring", stiffness: 200, damping: 18 }}
                      className="inline-block text-[#FF4500]"
                    >
                      {FINAL_LINE.highlight}.
                    </motion.span>
                  </motion.h2>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.7, duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                    className="h-[2px] w-24 sm:w-32 bg-[#FF4500] origin-left mt-2"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IntroOverlay;
